/**
 * DICOM Viewer Page
 *
 * Full-screen medical image viewer with integrated report panel
 * Optimized for radiologist workflow - view images and write report simultaneously
 *
 * Design principles:
 * - Panel always visible by default (minimize clicks)
 * - Voice dictation always ready
 * - Patient/study context always visible
 * - Modern, clean interface
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  PanelRightClose,
  PanelRightOpen,
  Send,
  Mic,
  Square,
  Calendar,
  Hash,
  Building2,
  Stethoscope,
  CheckCircle,
  Paperclip,
  Eye,
  FileText,
  ChevronDown,
  Undo2,
  Save,
} from 'lucide-react'

import {
  useStudy,
  getStudyFileUrl,
  getStudyInstances,
  formatStudyDate,
  type StudyInstance,
  type SeriesInfo,
} from '@/application/studies'
import { urlsParaVisor } from '@/lib/media-url'
import { usePatient } from '@/application/patients'
import { usePermiso, useAuth } from '@/application/auth'
import { useCreateReport, useSignReport, useStudyReports, useUpdateReportStatus } from '@/application/reports'
import { useStudyAttachments, useDownloadAttachment, useUploadAttachment } from '@/application/study-attachments'
import { useTemplatesByModality } from '@/application/report-templates'
import { useWorklistItemByStudy, useAutoStartWorklist } from '@/application/worklist'
import { useDictation, useDictationAvailable, useGrabacionDeDictado, blobToBase64 } from '@/application/dictation'
import { DicomViewer } from '@/presentation/components/dicom'
import { ThumbnailPanel } from '@/presentation/components/dicom/ThumbnailPanel'
import { ReturnStudyModal } from '@/presentation/components/worklist'
import { ControlesDeDictado } from '@/presentation/components/dictation/ControlesDeDictado'
import { AttachmentThumbnail } from '@/presentation/components/viewer/AttachmentThumbnail'
import { Button } from '@/presentation/components/ui/Button'
import { canReturnWorklistItem } from '@/domain/worklist'
import { MODALITY_LABELS, BIRADS_CATEGORIES, BIRADS_SHORT_LABELS, type Modality } from '@/config/constants'
import { cn } from '@/lib/utils'
import { getErrorMessage } from '@/lib/api'
import { toast } from '@/lib/toast'
import type { ReportTemplate } from '@/domain/report-template/types'

// =============================================================================
// Types
// =============================================================================

type ReportSection = 'technique' | 'findings' | 'conclusions'

interface Study {
  id: string
  studyDescription: string | null
  modality: string
  studyDate: string
  accessionNumber: string | null
  institutionName: string | null
  referringPhysician: string | null
  numberOfSeries: number
  numberOfInstances: number
}

interface Patient {
  id: string
  firstName: string
  lastName: string
  birthDate: string
  gender: string
  document: { type: string; number: string }
}

interface StudyAttachment {
  id: string
  studyId: string
  attachmentType: string
  fileName: string
  fileSize: number | null
  mimeType: string | null
  description: string | null
}

// =============================================================================
// Voice Dictation Hook (Integrated)
// =============================================================================

function useIntegratedDictation(
  activeSection: ReportSection | null,
  onTranscript: (text: string) => void
) {
  const isSupported = useDictationAvailable()

  const { isListening, grabando, transcribiendo, pausado, transcript, startListening, stopListening, pausar, cancelar } = useDictation({
    lang: 'es-CO',
    processCommands: true,
    onResult: onTranscript,
    /**
     * El fallo del dictado se AVISA, no se traga.
     *
     * Antes este hook no tomaba `error` siquiera: si el navegador negaba el
     * microfono o el servicio no respondia, el radiologo pulsaba el boton y no
     * pasaba nada, sin una sola pista de por que. Es el mismo defecto que se
     * vino a corregir, solo que con otra causa, y en la pantalla donde el
     * especialista trabaja de verdad.
     */
    onError: (mensaje) => toast.error(mensaje),
  })

  const toggle = useCallback(() => {
    if (!activeSection) return
    if (isListening) {
      stopListening()
    } else {
      startListening()
    }
  }, [activeSection, isListening, startListening, stopListening])

  return { isSupported, isListening, grabando, transcribiendo, pausado, transcript, toggle, stop: stopListening, pausar, cancelar }
}

// =============================================================================
// Integrated Report Panel Component (Always Visible)
// =============================================================================

interface ReportPanelProps {
  study: Study
  patient: Patient | undefined
  onClose: () => void
  onReportCreated: () => void
}

/**
 * Microfono de UNA seccion del informe.
 *
 * POR QUE VA AQUI Y NO ARRIBA
 * ===========================
 * Antes habia un unico microfono en la cabecera del panel y un concepto de
 * "seccion activa" que el radiologo tenia que llevar en la cabeza. Estando en
 * Conclusiones, dictar exigia subir hasta la cabecera, pulsar y volver a bajar.
 *
 * El problema no era donde estaba el boton: era que existiera un modo global
 * que hay que sincronizar con el sitio donde se escribe. Con un microfono por
 * caja, se pulsa donde se esta y dicta ahi. Se acaba el viaje y se acaba el
 * concepto.
 */
function MicrofonoDeSeccion({
  seccion,
  activa,
  grabando,
  disponible,
  onAlternar,
}: {
  seccion: ReportSection
  activa: boolean
  grabando: boolean
  disponible: boolean
  onAlternar: (seccion: ReportSection) => void
}) {
  if (!disponible) return null

  const dictandoAqui = activa && grabando

  return (
    <button
      type="button"
      onClick={() => onAlternar(seccion)}
      title={dictandoAqui ? 'Detener el dictado' : 'Dictar en este campo (Ctrl+M)'}
      aria-label={dictandoAqui ? 'Detener el dictado' : 'Dictar en este campo'}
      className={cn(
        'flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-all',
        dictandoAqui
          ? 'bg-red-500/20 text-red-400'
          : 'text-slate-500 hover:bg-slate-700/50 hover:text-slate-300'
      )}
    >
      {dictandoAqui ? (
        <>
          <Square className="h-3 w-3 fill-current" />
          <span className="animate-pulse">Dictando</span>
        </>
      ) : (
        <>
          <Mic className="h-3 w-3" />
          <span>Dictar</span>
        </>
      )}
    </button>
  )
}

function IntegratedReportPanel({ study, patient, onClose, onReportCreated }: ReportPanelProps) {
  const [activeSection, setActiveSection] = useState<ReportSection>('findings')
  const textareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({})
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false)
  const templateDropdownRef = useRef<HTMLDivElement>(null)

  // Attachments
  const { data: attachmentsData } = useStudyAttachments(study.id)
  const downloadMutation = useDownloadAttachment()
  const subirAdjunto = useUploadAttachment()

  const grabacion = useGrabacionDeDictado((mensaje) => toast.error(mensaje))

  const guardarDictado = async () => {
    if (!grabacion.grabando) {
      await grabacion.iniciar()
      return
    }

    const audio = await grabacion.detener()
    if (!audio) return

    try {
      await subirAdjunto.mutateAsync({
        studyId: study.id,
        data: {
          attachmentType: 'DICTATION_AUDIO',
          fileName: `dictado-${Date.now()}.webm`,
          fileData: await blobToBase64(audio),
          mimeType: 'audio/webm',
          description: 'Dictado grabado, pendiente de transcribir',
        },
      })
      toast.success('Dictado guardado. Queda adjunto al estudio para transcribir')
    } catch (err) {
      toast.error(getErrorMessage(err, 'No se pudo guardar el dictado'))
    }
  }
  const attachments = attachmentsData?.attachments || []

  // Templates by modality
  const { data: templatesData } = useTemplatesByModality(study.modality)
  const templates = templatesData?.templates || []

  const [formData, setFormData] = useState({
    title: study.studyDescription ? `Informe de ${study.studyDescription}` : 'Informe Radiológico',
    technique: '',
    findings: '',
    conclusions: '',
    biradsCategory: '' as string,
  })

  // Check if mammography (requires BIRADS)
  const isMammography = study.modality === 'MG'
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Close template dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(event.target as Node)) {
        setShowTemplateDropdown(false)
      }
    }
    if (showTemplateDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showTemplateDropdown])

  // Apply template
  const applyTemplate = useCallback((template: ReportTemplate) => {
    setFormData((prev) => ({
      ...prev,
      findings: template.findingsTemplate,
      conclusions: template.conclusionsTemplate || prev.conclusions,
      technique: template.technicalNotesTemplate || prev.technique,
    }))
    setShowTemplateDropdown(false)
    // Focus on findings field after applying
    setTimeout(() => {
      textareaRefs.current.findings?.focus()
    }, 50)
  }, [])

  const createMutation = useCreateReport()
  const signMutation = useSignReport()
  const estadoMutation = useUpdateReportStatus()

  /**
   * Seccion a la que pertenece el dictado en curso.
   *
   * EL DEFECTO QUE CIERRA
   * =====================
   * El texto transcrito se escribia en `activeSection`, que sigue al foco.
   * Al detener el dictado el motor entrega todavia el ultimo trozo de audio
   * ya grabado, y ese texto llegaba SEGUNDOS DESPUES. Si para entonces el
   * radiologo habia pasado a Conclusiones, que es justo lo que se hace al
   * terminar los hallazgos, la frase aterrizaba dentro de la impresion
   * diagnostica.
   *
   * Comprobado el 21 de septiembre de 2026: una frase dictada para Hallazgos
   * acabo dentro de las Conclusiones de un informe firmado, y de ahi paso al
   * portal del paciente. La impresion diagnostica es la parte del informe con
   * valor legal.
   *
   * Es una referencia y no un estado porque no pinta nada: solo tiene que
   * estar disponible cuando llegue el texto, sin volver a crear el manejador
   * ni perder el destino entre repintados.
   */
  const seccionDictada = useRef<ReportSection | null>(null)

  const handleDictation = useCallback((text: string) => {
    // El destino es donde se empezo a dictar, no donde este el cursor ahora.
    const destino = seccionDictada.current ?? activeSection
    setFormData((prev) => {
      const currentValue = prev[destino] || ''
      const separator = currentValue && !currentValue.endsWith(' ') && !currentValue.endsWith('\n') ? ' ' : ''
      return { ...prev, [destino]: currentValue + separator + text }
    })
    const textarea = textareaRefs.current[destino]
    if (textarea) {
      setTimeout(() => { textarea.scrollTop = textarea.scrollHeight }, 50)
    }
  }, [activeSection])

  const dictation = useIntegratedDictation(activeSection, handleDictation)

  // Auto-focus first field
  useEffect(() => {
    const textarea = textareaRefs.current.findings
    if (textarea) textarea.focus()
  }, [])

  const handleChange = useCallback((field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }))
  }, [errors])

  const handleSectionFocus = useCallback((section: ReportSection) => {
    setActiveSection(section)
  }, [])

  /**
   * Dicta en la seccion indicada. Si ya estaba dictando ahi, se detiene; si
   * estaba dictando en otra, se cambia sin que el radiologo tenga que parar
   * y volver a empezar.
   */
  const alternarDictadoEn = useCallback(
    (seccion: ReportSection) => {
      const mismaSeccion = activeSection === seccion

      if (dictation.isListening && mismaSeccion) {
        // No se borra `seccionDictada`: el ultimo trozo de audio llega
        // despues de detener, y tiene que caer en el campo que se estaba
        // dictando, no en el que el radiologo mire a continuacion.
        dictation.stop()
        return
      }

      setActiveSection(seccion)
      seccionDictada.current = seccion
      textareaRefs.current[seccion]?.focus()

      if (!dictation.isListening) dictation.toggle()
    },
    [activeSection, dictation]
  )

  // Ctrl/Cmd + M dicta en el campo donde esta el cursor. El radiologo trabaja
  // con las manos en el teclado; obligarle a buscar un boton con el raton en
  // mitad de un informe es tiempo perdido en cada estudio.
  useEffect(() => {
    const alPulsar = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'm') {
        e.preventDefault()
        alternarDictadoEn(activeSection)
      }
    }
    window.addEventListener('keydown', alPulsar)
    return () => window.removeEventListener('keydown', alPulsar)
  }, [activeSection, alternarDictadoEn])

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!formData.findings.trim()) newErrors.findings = 'Requerido'
    if (!formData.conclusions.trim()) newErrors.conclusions = 'Requerido'
    // BIRADS required for mammography
    if (isMammography && !formData.biradsCategory) {
      newErrors.biradsCategory = 'BIRADS es obligatorio para mamografías'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  /**
   * Redactar, completar y suscribir son actos distintos (DICOM SR separa
   * completitud de verificacion), asi que el radiologo elige con cual sale.
   *
   * Crear siempre deja el informe en borrador. Si el segundo acto falla, lo
   * escrito ya esta a salvo y la pantalla lo dice: perder un informe recien
   * redactado por un fallo de red es lo que no se puede permitir.
   */
  const guardarInforme = async (cierre: 'borrador' | 'transcribir' | 'firmar') => {
    if (!validate()) return

    let informeCreado
    try {
      informeCreado = await createMutation.mutateAsync({
        studyId: study.id,
        title: formData.title,
        findings: formData.technique
          ? `TÉCNICA:\n${formData.technique}\n\nHALLAZGOS:\n${formData.findings}`
          : formData.findings,
        impression: formData.conclusions,
        biradsCategory: isMammography ? formData.biradsCategory : undefined,
      })
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al guardar el informe'))
      return
    }

    if (cierre === 'borrador') {
      toast.success('Informe guardado como borrador, sin firmar')
      onReportCreated()
      return
    }

    try {
      if (cierre === 'firmar') {
        await signMutation.mutateAsync(informeCreado.id)
        toast.success('Informe firmado correctamente')
      } else {
        await estadoMutation.mutateAsync({ id: informeCreado.id, status: 'TRANSCRIBING' })
        toast.success('Informe enviado a transcripción')
      }
      onReportCreated()
    } catch (err) {
      toast.error(
        getErrorMessage(
          err,
          'El informe quedó guardado como borrador, pero no se pudo completar la acción'
        )
      )
    }
  }

  // Handle attachment view/download
  const handleViewAttachment = async (attachment: StudyAttachment) => {
    try {
      const blob = await downloadMutation.mutateAsync({
        studyId: study.id,
        attachmentId: attachment.id,
      })
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al descargar el archivo')
    }
  }

  const isSaving = createMutation.isPending

  // Calculate patient age
  const getAge = (birthDate: string) => {
    const today = new Date()
    const birth = new Date(birthDate)
    let age = today.getFullYear() - birth.getFullYear()
    const m = today.getMonth() - birth.getMonth()
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
    return age
  }

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-slate-900 to-slate-950">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-b border-slate-700/50 bg-slate-900/80 backdrop-blur">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-sm font-medium text-white">Nuevo Informe</span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors"
          title="Ocultar panel"
        >
          <PanelRightClose className="h-4 w-4" />
        </button>
      </div>

      {/* Patient & Study Info */}
      <div className="flex-shrink-0 p-3 border-b border-slate-700/50 bg-slate-800/30">
        {patient && (
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
              {patient.firstName[0]}{patient.lastName[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm truncate">
                {patient.firstName} {patient.lastName}
              </p>
              <p className="text-slate-400 text-xs">
                {getAge(patient.birthDate)} años • {patient.gender === 'M' ? 'Masculino' : 'Femenino'} • {patient.document.number}
              </p>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-1.5 text-slate-400">
            <Stethoscope className="h-3 w-3" />
            <span className="truncate">{MODALITY_LABELS[study.modality as Modality] || study.modality}</span>
          </div>
          <div className="flex items-center gap-1.5 text-slate-400">
            <Calendar className="h-3 w-3" />
            <span>{formatStudyDate(study.studyDate)}</span>
          </div>
          {study.accessionNumber && (
            <div className="flex items-center gap-1.5 text-slate-400">
              <Hash className="h-3 w-3" />
              <span className="truncate">{study.accessionNumber}</span>
            </div>
          )}
          {study.institutionName && (
            <div className="flex items-center gap-1.5 text-slate-400">
              <Building2 className="h-3 w-3" />
              <span className="truncate">{study.institutionName}</span>
            </div>
          )}
        </div>
      </div>

      {/* Attachments - Thumbnail Grid */}
      {attachments.length > 0 && (
        <div className="flex-shrink-0 p-2 border-b border-slate-700/50 bg-slate-800/20">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Paperclip className="h-3 w-3 text-slate-400" />
              <span className="text-[10px] font-medium text-slate-300">
                Adjuntos
              </span>
            </div>
            <span className="text-[9px] text-slate-500 bg-slate-700/50 px-1 py-0.5 rounded">
              {attachments.length}
            </span>
          </div>

          {/* Thumbnails - fixed size, wrap */}
          <div className="flex flex-wrap gap-1.5">
            {attachments.map((attachment) => (
              <button
                key={attachment.id}
                onClick={() => handleViewAttachment(attachment)}
                disabled={downloadMutation.isPending}
                title={attachment.fileName}
                className={cn(
                  'relative w-12 h-12 rounded overflow-hidden flex-shrink-0',
                  'bg-slate-700/50 border border-slate-600/50',
                  'hover:border-blue-500/50 hover:ring-1 hover:ring-blue-500/30',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500/50',
                  'transition-all duration-200 group',
                  downloadMutation.isPending && 'opacity-50 cursor-wait'
                )}
              >
                {/* Thumbnail */}
                <AttachmentThumbnail
                  studyId={study.id}
                  attachmentId={attachment.id}
                  mimeType={attachment.mimeType}
                  fileName={attachment.fileName}
                />

                {/* Badge de tipo - esquina superior izquierda */}
                <div className="absolute top-0 left-0 z-10">
                  <span
                    className={cn(
                      'px-0.5 text-[6px] font-bold rounded-br',
                      attachment.attachmentType === 'CLINICAL_HISTORY'
                        ? 'bg-green-500 text-white'
                        : attachment.attachmentType === 'MEDICAL_ORDER'
                          ? 'bg-blue-500 text-white'
                          : attachment.attachmentType === 'PREVIOUS_RESULTS'
                            ? 'bg-amber-500 text-white'
                            : 'bg-slate-600 text-slate-200'
                    )}
                  >
                    {attachment.attachmentType === 'CLINICAL_HISTORY'
                      ? 'HC'
                      : attachment.attachmentType === 'MEDICAL_ORDER'
                        ? 'OM'
                        : attachment.attachmentType === 'PREVIOUS_RESULTS'
                          ? 'RP'
                          : 'ADJ'}
                  </span>
                </div>

                {/* Overlay de hover */}
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Eye className="h-4 w-4 text-white" />
                </div>

                {/* Indicador de descarga en progreso */}
                {downloadMutation.isPending && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Loader2 className="h-3.5 w-3.5 text-white animate-spin" />
                  </div>
                )}
              </button>
            ))}
          </div>

        </div>
      )}

      {/* Voice Dictation */}
      {dictation.isSupported && (
        <div className="flex-shrink-0 p-3 border-b border-slate-700/50 bg-slate-800/20">
          <div className="flex items-center gap-3">
            <button
              onClick={dictation.toggle}
              disabled={isSaving}
              className={cn(
                "relative w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300",
                dictation.isListening
                  ? "bg-red-500 text-white shadow-lg shadow-red-500/30 scale-110"
                  : "bg-gradient-to-br from-blue-500 to-purple-600 text-white hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105"
              )}
            >
              {dictation.isListening ? (
                <Square className="h-5 w-5 fill-current" />
              ) : (
                <Mic className="h-5 w-5" />
              )}
              {dictation.isListening && (
                <span className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-30" />
              )}
            </button>
            <div className="flex-1 min-w-0">
              {dictation.isListening ? (
                <div>
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                    </span>
                    <span className="text-red-400 text-xs font-medium">Grabando...</span>
                  </div>
                  {dictation.transcript && (
                    <p className="text-slate-300 text-xs mt-1 truncate italic">{dictation.transcript}</p>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-white text-xs font-medium">Dictado por Voz</p>
                  <p className="text-slate-500 text-xs">
                    Dictando en: <span className="text-blue-400">{
                      activeSection === 'technique' ? 'Informe Técnico' :
                      activeSection === 'findings' ? 'Hallazgos' : 'Conclusiones'
                    }</span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Form Content - Solo 4 campos: Título, Informe Técnico, Hallazgos, Conclusiones */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {/* Título */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-400">Título</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Título del informe..."
            className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 focus:outline-none transition-all"
          />
        </div>

        {/* Informe Técnico */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-slate-400">Informe Técnico</label>
            <MicrofonoDeSeccion
              seccion="technique"
              activa={activeSection === 'technique'}
              grabando={dictation.grabando}
              disponible={dictation.isSupported}
              onAlternar={alternarDictadoEn}
            />
          </div>
          <textarea
            ref={(el) => { textareaRefs.current.technique = el }}
            value={formData.technique}
            onChange={(e) => handleChange('technique', e.target.value)}
            onFocus={() => handleSectionFocus('technique')}
            placeholder="Protocolo y parámetros técnicos..."
            rows={3}
            className={cn(
              "w-full bg-slate-800/50 border rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 resize-none focus:outline-none transition-all",
              activeSection === 'technique'
                ? "border-blue-500/50 ring-1 ring-blue-500/20"
                : "border-slate-700/50 hover:border-slate-600"
            )}
          />
        </div>

        {/* Template Selector - Always visible */}
        {templates.length > 0 && (
          <div ref={templateDropdownRef} className="relative">
            <button
              type="button"
              onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors border border-blue-500/30"
            >
              <FileText className="h-4 w-4" />
              Usar Plantilla ({templates.length} disponibles)
              <ChevronDown className={cn("h-4 w-4 transition-transform ml-auto", showTemplateDropdown && "rotate-180")} />
            </button>
            {showTemplateDropdown && (
              <div className="absolute left-0 right-0 top-full mt-1 max-h-64 overflow-y-auto bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50">
                <div className="p-2 border-b border-slate-700">
                  <p className="text-xs text-slate-400">Seleccionar plantilla para {study.modality}</p>
                </div>
                <div className="py-1">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => applyTemplate(template)}
                      className="w-full flex flex-col items-start px-3 py-2 text-left hover:bg-slate-700/50 transition-colors"
                    >
                      <span className="text-sm text-white font-medium">{template.name}</span>
                      {template.category && (
                        <span className="text-xs text-slate-500">{template.category}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Hallazgos */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-slate-400">
              Hallazgos <span className="text-red-400">*</span>
            </label>
            <MicrofonoDeSeccion
              seccion="findings"
              activa={activeSection === 'findings'}
              grabando={dictation.grabando}
              disponible={dictation.isSupported}
              onAlternar={alternarDictadoEn}
            />
          </div>
          <textarea
            ref={(el) => { textareaRefs.current.findings = el }}
            value={formData.findings}
            onChange={(e) => handleChange('findings', e.target.value)}
            onFocus={() => handleSectionFocus('findings')}
            placeholder="Describa los hallazgos observados..."
            rows={5}
            className={cn(
              "w-full bg-slate-800/50 border rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 resize-none focus:outline-none transition-all",
              activeSection === 'findings'
                ? "border-blue-500/50 ring-1 ring-blue-500/20"
                : "border-slate-700/50 hover:border-slate-600",
              errors.findings && "border-red-500/50 ring-1 ring-red-500/20"
            )}
          />
        </div>

        {/* Conclusiones */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-slate-400">
              Conclusiones <span className="text-red-400">*</span>
            </label>
            <MicrofonoDeSeccion
              seccion="conclusions"
              activa={activeSection === 'conclusions'}
              grabando={dictation.grabando}
              disponible={dictation.isSupported}
              onAlternar={alternarDictadoEn}
            />
          </div>
          <textarea
            ref={(el) => { textareaRefs.current.conclusions = el }}
            value={formData.conclusions}
            onChange={(e) => handleChange('conclusions', e.target.value)}
            onFocus={() => handleSectionFocus('conclusions')}
            placeholder="Diagnóstico y conclusiones..."
            rows={4}
            className={cn(
              "w-full bg-slate-800/50 border rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 resize-none focus:outline-none transition-all",
              activeSection === 'conclusions'
                ? "border-blue-500/50 ring-1 ring-blue-500/20"
                : "border-slate-700/50 hover:border-slate-600",
              errors.conclusions && "border-red-500/50 ring-1 ring-red-500/20"
            )}
          />
        </div>

        {/* BIRADS Selector - Only for Mammography */}
        {isMammography && (
          <div className="space-y-1 p-3 bg-pink-500/10 border border-pink-500/30 rounded-lg">
            <label className="text-xs font-medium text-pink-300 flex items-center gap-2">
              <span className="px-1.5 py-0.5 bg-pink-500 text-white text-[10px] font-bold rounded">BIRADS</span>
              Clasificación <span className="text-red-400">*</span>
            </label>
            <select
              value={formData.biradsCategory}
              onChange={(e) => setFormData(prev => ({ ...prev, biradsCategory: e.target.value }))}
              className={cn(
                "w-full bg-slate-800 border rounded-lg px-3 py-2 text-sm text-white focus:outline-none transition-all",
                formData.biradsCategory
                  ? "border-pink-500/50 ring-1 ring-pink-500/20"
                  : "border-slate-700/50",
                errors.biradsCategory && "border-red-500/50 ring-1 ring-red-500/20"
              )}
            >
              <option value="">Seleccione categoría BIRADS...</option>
              {Object.entries(BIRADS_CATEGORIES).map(([key, value]) => (
                <option key={key} value={value}>
                  {BIRADS_SHORT_LABELS[value as keyof typeof BIRADS_SHORT_LABELS]}
                </option>
              ))}
            </select>
            {errors.biradsCategory && (
              <p className="text-xs text-red-400 mt-1">{errors.biradsCategory}</p>
            )}
          </div>
        )}

        {/* Error message */}
        {createMutation.isError && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-300 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {createMutation.error?.message || 'Error al guardar'}
          </div>
        )}
      </div>

      {(dictation.grabando || dictation.transcribiendo || dictation.pausado) && (
        <div className="flex-shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-t border-slate-700/50 bg-slate-900/80">
          {dictation.grabando ? (
            <span className="flex items-center gap-2 text-xs font-medium text-red-400">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
              </span>
              Grabando
            </span>
          ) : dictation.transcribiendo ? (
            <span className="flex items-center gap-2 text-xs font-medium text-sky-400">
              <Loader2 className="h-3 w-3 animate-spin" />
              Micrófono cerrado · transcribiendo
            </span>
          ) : (
            <span className="text-xs font-medium text-amber-400">
              En pausa · el micrófono está cerrado
            </span>
          )}
          <ControlesDeDictado
            grabando={dictation.grabando}
            transcribiendo={dictation.transcribiendo}
            pausado={dictation.pausado}
            onPausar={dictation.pausar}
            onReanudar={dictation.toggle}
            onDetener={dictation.stop}
            onCancelar={dictation.cancelar}
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex-shrink-0 p-3 border-t border-slate-700/50 bg-slate-900/80 backdrop-blur space-y-2">
        <button
          onClick={() => guardarInforme('firmar')}
          disabled={isSaving}
          className={cn(
            "w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm transition-all",
            isSaving
              ? "bg-slate-700 text-slate-400 cursor-not-allowed"
              : "bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/30"
          )}
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          Firmar y Enviar
        </button>

        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => guardarInforme('borrador')}
            disabled={isSaving}
            className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-slate-600 text-slate-200 text-sm font-medium hover:bg-slate-800 disabled:opacity-40 transition-colors"
          >
            <Save className="h-4 w-4" />
            Borrador
          </button>
          <button
            onClick={() => guardarInforme('transcribir')}
            disabled={isSaving}
            className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-slate-600 text-slate-200 text-sm font-medium hover:bg-slate-800 disabled:opacity-40 transition-colors"
          >
            <Send className="h-4 w-4" />
            A transcribir
          </button>
        </div>

        {grabacion.disponible && (
          <button
            onClick={guardarDictado}
            disabled={subirAdjunto.isPending}
            className={cn(
              'w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-colors',
              grabacion.grabando
                ? 'bg-red-500/20 text-red-300 border border-red-500/40'
                : 'border border-slate-600 text-slate-200 hover:bg-slate-800'
            )}
          >
            {subirAdjunto.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : grabacion.grabando ? (
              <Square className="h-4 w-4 fill-current" />
            ) : (
              <Mic className="h-4 w-4" />
            )}
            {grabacion.grabando
              ? `Detener y guardar · ${String(Math.floor(grabacion.segundos / 60)).padStart(2, '0')}:${String(grabacion.segundos % 60).padStart(2, '0')}`
              : 'Grabar dictado sin transcribir'}
          </button>
        )}

        <p className="text-[11px] leading-snug text-slate-400 text-center">
          Un informe sin firmar no le llega al paciente ni al médico remitente.
        </p>
      </div>
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function ViewerPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const puedeAnotar = usePermiso('annotation:create')
  const puedeInformar = usePermiso('report:create')

  const { data: study, isLoading: studyLoading, isError, error, refetch } = useStudy(id)
  const { data: patient } = usePatient(study?.patientId)
  const { data: existingReports } = useStudyReports(id)

  // Auto-start worklist item when opening the viewer
  const { data: worklistItem } = useWorklistItemByStudy(id)
  const { autoStart } = useAutoStartWorklist()
  const [hasAutoStarted, setHasAutoStarted] = useState(false)

  /**
   * Marcar el estudio "En Lectura" al abrirlo, pero SOLO quien lo va a leer.
   *
   * Antes se hacia para cualquiera que abriera el visor. Dos problemas:
   *
   *   - El servidor lo niega a quien no es el asignado (la ruta exige
   *     `worklist:view:own` y el handler comprueba la propiedad), asi que un
   *     ADMIN supervisando dejaba un 403 en cada apertura. Es una pantalla
   *     pidiendo algo que le van a negar, que es justo lo que vigila
   *     `03-pantallas-sin-acceso-denegado`.
   *   - Y aunque se le permitiera, estaria mal: "En Lectura" significa que el
   *     radiologo empezo a leer. Que un administrador se asome no cambia el
   *     estado clinico del estudio.
   *
   * No se veia porque el aprovisionamiento de demostracion no creaba listas de
   * trabajo: sin elementos que arrancar, la llamada nunca salia.
   */
  const esMiEstudio = !!worklistItem && worklistItem.assignedTo === user?.id

  useEffect(() => {
    if (worklistItem && esMiEstudio && !hasAutoStarted && worklistItem.status === 'PENDING') {
      setHasAutoStarted(true)
      autoStart(worklistItem)
    }
  }, [worklistItem, esMiEstudio, hasAutoStarted, autoStart])

  // Panel visible by default (hide only if user wants)
  const [showReportPanel, setShowReportPanel] = useState(true)

  /**
   * En un telefono NO caben imagen e informe a la vez.
   *
   * La pantalla esta pensada para dos monitores. Al hacerla responsiva se
   * partio por la mitad, y en 664px de alto el panel del informe tenia que
   * albergar datos del paciente, adjuntos, dictado, cuatro campos y el boton
   * de firmar. No cabe, y no cabe por mucho que se ajusten margenes: en el
   * movil no se podia redactar un informe, que es justo para lo que existe
   * esta pantalla.
   *
   * La forma correcta en un telefono es alternar: o miras la imagen, o
   * escribes, y cada cosa dispone de la pantalla completa. En pantalla ancha
   * esto no aplica y se sigue viendo todo a la vez.
   */
  const [vistaMovil, setVistaMovil] = useState<'imagen' | 'informe'>('imagen')

  // Multi-image support: instances and current index
  const [instances, setInstances] = useState<StudyInstance[]>([])
  const [seriesInfo, setSeriesInfo] = useState<SeriesInfo[]>([])
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [instancesLoading, setInstancesLoading] = useState(true) // Start true to prevent fallback URL on first render

  // Fetch study instances when study is loaded
  useEffect(() => {
    if (!id || !study) return

    const fetchInstances = async () => {
      setInstancesLoading(true)
      try {
        const response = await getStudyInstances(id)
        setInstances(response.instances)
        setSeriesInfo(response.series)
        console.log(`[ViewerPage] Loaded ${response.totalInstances} instances from ${response.series.length} series`)
      } catch (err) {
        console.warn('[ViewerPage] Could not load study instances:', err)
        // Fall back to single image mode - instances will be empty
      } finally {
        setInstancesLoading(false)
      }
    }

    fetchInstances()
  }, [id, study])

  // Check if study already has a report
  const hasReport = study?.reportId || (existingReports && existingReports.length > 0)
  const existingReportId = study?.reportId || existingReports?.[0]?.id

  // Return study modal
  const [showReturnModal, setShowReturnModal] = useState(false)
  // Can only return if worklist item allows it AND there's no signed report
  const canReturn = worklistItem && canReturnWorklistItem(worklistItem.status) && !hasReport

  // Build image URLs - prefer instances (multiple images), fallback to single file
  // Wait for instances to load before falling back to single file URL
  //
  // La credencial la pone `urlsParaVisor`, el mismo sitio que usa el portal
  // del paciente. Antes se concatenaba aqui a mano y en el panel de
  // miniaturas por separado, siempre con la credencial del PERSONAL: por eso
  // esos componentes no servian para el portal.
  const imageUrls = instances.length > 0
    ? urlsParaVisor(instances.map(inst => inst.url), 'personal')
    : instancesLoading
      ? [] // Don't use fallback while instances are still loading
      : (id ? [getStudyFileUrl(id)].filter(Boolean) as string[] : [])

  const handleReportCreated = () => {
    refetch() // Refresh study to get updated reportId
  }

  if (studyLoading) {
    return (
      <div className="h-screen alto-de-pantalla-fijo w-screen bg-black flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Cargando estudio...</p>
        </div>
      </div>
    )
  }

  if (isError || !study) {
    return (
      <div className="h-screen alto-de-pantalla-fijo w-screen bg-black flex items-center justify-center">
        <div className="text-center text-white">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{error?.message || 'Estudio no encontrado'}</p>
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen alto-de-pantalla-fijo w-screen bg-black flex flex-col">
      {/* Minimal Header */}
      <div className="flex-shrink-0 bg-slate-900/95 backdrop-blur border-b border-slate-700/50 px-4 py-2">
        <div className="flex items-center justify-between gap-2">
          {/* Left - Back & Study.
              `min-w-0` para que el titulo pueda encogerse: sin esto la
              cabecera crecia, el titulo se partia en varias lineas y el
              boton de la derecha quedaba cortado fuera de la pantalla. */}
          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="min-w-0">
              <h1 className="text-white font-medium text-sm truncate">
                {study.studyDescription || 'Estudio'}
              </h1>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span className="px-1.5 py-0.5 bg-slate-700/50 rounded text-slate-300 shrink-0">
                  {MODALITY_LABELS[study.modality as Modality] || study.modality}
                </span>
                {/* El recuento se oculta en telefono: es dato de contexto y
                    lo que hace falta ahi es el titulo y las acciones. */}
                <span className="hidden sm:inline whitespace-nowrap">
                  {study.numberOfSeries} series • {study.numberOfInstances} imgs
                </span>
              </div>
            </div>
          </div>

          {/* Right - Status & Panel Toggle */}
          <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
            {/* Alternar entre imagen e informe. Solo en telefono: ahi no
                caben las dos cosas y hay que elegir cual ocupa la pantalla. */}
            {puedeInformar && showReportPanel && !hasReport && (
              <div className="lg:hidden flex items-center rounded-lg bg-slate-800/80 p-0.5">
                <button
                  onClick={() => setVistaMovil('imagen')}
                  aria-pressed={vistaMovil === 'imagen'}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                    vistaMovil === 'imagen'
                      ? 'bg-slate-700 text-white'
                      : 'text-slate-400'
                  )}
                >
                  Imagen
                </button>
                <button
                  onClick={() => setVistaMovil('informe')}
                  aria-pressed={vistaMovil === 'informe'}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                    vistaMovil === 'informe'
                      ? 'bg-slate-700 text-white'
                      : 'text-slate-400'
                  )}
                >
                  Informe
                </button>
              </div>
            )}

            {/* Return Study Button - only show if can return */}
            {canReturn && (
              <button
                onClick={() => setShowReturnModal(true)}
                className="flex items-center gap-2 px-2 md:px-3 py-1.5 rounded-lg bg-yellow-500/20 text-yellow-400 text-sm font-medium hover:bg-yellow-500/30 transition-colors shrink-0"
                title="Devolver estudio"
              >
                <Undo2 className="h-4 w-4 shrink-0" />
                <span className="hidden md:inline">Devolver</span>
              </button>
            )}

            {hasReport ? (
              <Link to={`/reports/${existingReportId}`}>
                <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 text-sm font-medium hover:bg-emerald-500/30 transition-colors">
                  <CheckCircle className="h-4 w-4" />
                  Informe Firmado
                </button>
              </Link>
            ) : !showReportPanel && (
              <button
                onClick={() => setShowReportPanel(true)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-400 text-sm font-medium hover:bg-blue-500/30 transition-colors"
              >
                <PanelRightOpen className="h-4 w-4" />
                Mostrar Panel
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content
          En pantalla ancha, imagen y panel de informe van lado a lado, que es
          como trabaja el radiologo en sus dos monitores. Por debajo de `lg`
          se apilan: con el panel fijo de 420px al lado, en una tableta no
          quedaba sitio para ver la imagen, y la imagen es el producto. */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Thumbnail Panel (left side) - only show if multiple images */}
        {instances.length > 1 && (
          <ThumbnailPanel
            instances={instances}
            portal="personal"
            series={seriesInfo}
            currentIndex={currentImageIndex}
            onSelect={setCurrentImageIndex}
            isLoading={instancesLoading}
            position="left"
          />
        )}

        {/* Viewer. `min-h-0` para que un hijo de flex pueda encogerse. */}
        <div
          className={cn(
            'flex-1 relative min-h-0',
            // En movil solo se muestra si la vista elegida es la imagen.
            vistaMovil === 'informe' && puedeInformar && showReportPanel && !hasReport
              ? 'hidden lg:block'
              : ''
          )}
        >
          <DicomViewer
            studyId={id!}
            imageUrls={imageUrls}
            showToolbar={true}
            // Medir y anotar es un acto reservado: lo decide el permiso, no
            // el hecho de haber abierto el visor. Un tecnologo o un
            // transcriptor llegan a esta misma pantalla y no lo tienen.
            puedeAnotar={puedeAnotar}
            onError={(err) => console.error('Viewer error:', err)}
            currentImageIndex={instances.length > 1 ? currentImageIndex : undefined}
            onImageIndexChange={instances.length > 1 ? setCurrentImageIndex : undefined}
          />
        </div>

        {/* Panel de informe: solo a quien puede redactarlo.
            Se ofrecia a cualquiera que abriera el visor, incluido el
            tecnologo, que recibe 403 al leer los informes y no puede firmar.
            Un formulario que el servidor va a rechazar no es una pantalla,
            es una trampa. Misma regla que las herramientas de medicion.

            SOBRE `lg:h-full`, QUE NO PUEDE SER `h-auto`
            ============================================
            Con `h-auto` el panel crece con su contenido en vez de estirarse a
            lo alto del contenedor. Al no tener tope, el area que se desplaza
            deja de acotarse y el ultimo campo, Conclusiones, que ademas es
            obligatorio, queda por debajo del boton de firmar y no se puede
            escribir. Se introdujo al hacer responsivo el visor el 2026-09-21
            y lo reporto Johannes el mismo dia. */}
        {puedeInformar && showReportPanel && !hasReport && (
          <div
            className={cn(
              'w-full h-full lg:w-[420px] lg:flex-shrink-0 border-slate-700/50 overflow-hidden',
              'lg:border-l',
              // En movil ocupa todo; se oculta mientras se mira la imagen.
              vistaMovil === 'imagen' ? 'hidden lg:block' : 'block'
            )}
          >
            <IntegratedReportPanel
              study={study as Study}
              patient={patient as Patient | undefined}
              onClose={() => setShowReportPanel(false)}
              onReportCreated={handleReportCreated}
            />
          </div>
        )}
      </div>

      {/* Return Study Modal */}
      <ReturnStudyModal
        open={showReturnModal}
        onOpenChange={setShowReturnModal}
        item={worklistItem || null}
        onSuccess={() => navigate(-1)}
      />
    </div>
  )
}
